// /lite ページのエントリポイント。
//   - 動画は Cloudflare Stream 公式 iframe player に丸投げ (= iOS background / lock screen /
//     PiP / Media Session API が iframe 内で全部動く)
//   - チャット + 参加者リスト + Visibility status は /、/club と共通の ChatRoom DO (/ws)
//   - ホーン (SE) は HTMLAudioElement で簡素実装 (= AudioContext 不要、foreground のみ鳴る)
//   - Web Audio EQ / Comp / Vintage は提供しない (= 直挿し / カジュアル聴取専用)

import { setupNameChangeModal } from "../shared/name-change-modal";
import { ParticipantsList } from "../shared/participants";
import { startVisibilityTracker, type VisibilityTracker } from "../shared/visibility-tracker";

interface ServerMessage {
  type:
    | "CHAT"
    | "PLAY_SE"
    | "SYSTEM"
    | "HEAT"
    | "USER_LIST"
    | "USER_JOIN"
    | "USER_LEAVE"
    | "USER_VISIBILITY";
  text?: string;
  seId?: string;
  user?: string;
  value?: number;
  ts: number;
  participants?: Array<{ name: string; count: number; visible: boolean }>;
  visible?: boolean;
}

let userId = "";
let ws: WebSocket | null = null;
let wsBackoffMs = 5000;
let participantsList: ParticipantsList | null = null;
let visibilityTracker: VisibilityTracker | null = null;
/** connectWs() が hidden 状態で呼ばれた時の visibilitychange listener が重複登録されないよう
 *  単一フラグで持つ (= / 側 main.ts の wsVisibilityWaiter と同パターン、Gemini medium 対応)。 */
let wsVisibilityWaiter: (() => void) | null = null;
/** iOS Safari の audio autoplay unlock 用フラグ。最初の user gesture (= login submit /
 *  ホーン click) 時に horn.play() を 1 度発火させて pause することで、以降の
 *  PLAY_SE broadcast 受信時の .play() が許可される (Gemini high 対応)。 */
let hornUnlocked = false;

async function boot(): Promise<void> {
  const me = await fetchMe();
  if (me) {
    userId = me.name;
    document.body.classList.remove("locked");
    setMeName(me.name);
    await initApp();
  } else {
    setupLogin();
  }
}

function setupLogin(): void {
  const form = document.getElementById("login-form") as HTMLFormElement;
  const nameEl = document.getElementById("login-name") as HTMLInputElement;
  const aikotobaEl = document.getElementById("login-aikotoba") as HTMLInputElement;
  const submitBtn = document.getElementById("login-submit") as HTMLButtonElement;
  const errEl = document.getElementById("login-error")!;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errEl.textContent = "";
    submitBtn.disabled = true;
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          name: nameEl.value.trim(),
          aikotoba: aikotobaEl.value,
        }),
      });
      if (res.status === 401) {
        errEl.textContent = "合言葉が違います";
        return;
      }
      if (!res.ok) {
        errEl.textContent = `エラー (${res.status})`;
        return;
      }
      const data = (await res.json()) as { name: string };
      userId = data.name;
      aikotobaEl.value = "";
      // iOS Safari の audio autoplay unlock を user gesture 内で発火させる
      // (= login submit は user click 経由なので gesture 有効)
      unlockHorn();
      document.body.classList.remove("locked");
      setMeName(data.name);
      await initApp();
    } catch {
      errEl.textContent = "通信エラー";
    } finally {
      submitBtn.disabled = false;
    }
  });
}

async function fetchMe(): Promise<{ name: string } | null> {
  try {
    const res = await fetch("/api/me", { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    return (await res.json()) as { name: string };
  } catch {
    return null;
  }
}

function setMeName(name: string): void {
  const el = document.getElementById("me-name")!;
  el.textContent = `as ${name}`;
  const logoutBtn = document.getElementById("logout-btn") as HTMLButtonElement;
  logoutBtn.hidden = false;
  logoutBtn.addEventListener("click", async () => {
    try {
      await fetch("/api/logout", { method: "POST", credentials: "same-origin" });
    } catch { /* ignore */ }
    location.reload();
  }, { once: true });
  // 名前変更 popover (= /、/club と共通の UX)
  setupNameChangeModal(name);
}

async function initApp(): Promise<void> {
  await loadIframePlayer();
  initSE();
  initChat();
}

async function loadIframePlayer(): Promise<void> {
  try {
    const res = await fetch("/api/get-iframe-url", { credentials: "same-origin" });
    if (!res.ok) {
      console.warn("[lite] iframe URL fetch failed", res.status);
      return;
    }
    const data = (await res.json()) as { iframeUrl: string };
    const iframe = document.getElementById("player") as HTMLIFrameElement;
    // about:blank の flash を避けるため、load 完了で .loaded class を付けて
    // CSS の opacity transition で fade-in (Browser API review medium 対応)。
    iframe.addEventListener(
      "load",
      () => iframe.classList.add("loaded"),
      { once: true },
    );
    iframe.src = data.iframeUrl;
  } catch (err) {
    console.warn("[lite] iframe URL fetch error", err);
  }
}

function initSE(): void {
  const horn = document.getElementById("se-horn") as HTMLAudioElement;
  const slider = document.getElementById("se-volume") as HTMLInputElement;
  const output = document.getElementById("se-volume-val") as HTMLOutputElement;

  // / と挙動を統一: 毎回 default 15%、localStorage 永続化なし。
  horn.volume = parseFloat(slider.value) / 100;
  slider.addEventListener("input", () => {
    const v = parseFloat(slider.value);
    output.value = String(v);
    horn.volume = v / 100;
  });

  // ホーン button: 自分が click → WS に PLAY_SE を投げる (= 全員に broadcast)
  // + iOS Safari の autoplay unlock を保証するため、click gesture 内で
  // 自分自身もすぐ play する (= broadcast loopback の play は async で gesture を
  // 失っているので iOS が reject する経路を回避、Gemini high 対応)。
  // 自分宛 broadcast は handleServerMessage 側で msg.user === userId で skip する
  // (= 二重再生防止)。
  document.querySelectorAll<HTMLButtonElement>('button[data-se]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const seId = btn.dataset.se;
      if (!seId || ws?.readyState !== WebSocket.OPEN) return;
      unlockHorn(); // ボタン click は user gesture なので確実に unlock
      ws.send(JSON.stringify({ type: "PLAY_SE", seId, user: userId }));
      // 自分の click ではローカル即時再生 (= 確実に gesture 内)
      if (seId === "horn") playHorn();
    });
  });
}

/** iOS Safari の autoplay 許可 unlock。最初の user gesture (= login submit /
 *  horn click) 内で horn.play() → pause を 1 度走らせると、以降の async な
 *  .play() (= WS PLAY_SE 受信時) も許可される。call は idempotent。 */
function unlockHorn(): void {
  if (hornUnlocked) return;
  hornUnlocked = true;
  const horn = document.getElementById("se-horn") as HTMLAudioElement | null;
  if (!horn) return;
  horn.load(); // mobile で preload="auto" が無視された時の保険
  // muted で 1 度 play → pause で audio engine を warm up
  const prevVolume = horn.volume;
  horn.volume = 0;
  horn.play()
    .then(() => {
      horn.pause();
      horn.currentTime = 0;
      horn.volume = prevVolume;
    })
    .catch(() => {
      horn.volume = prevVolume;
    });
}

function playHorn(): void {
  const horn = document.getElementById("se-horn") as HTMLAudioElement | null;
  if (!horn) return;
  horn.currentTime = 0;
  // background では .play() が rejected promise を返すので catch して無視
  // (= iOS は背景時に short audio を suspend する、これは仕様)
  horn.play().catch(() => { /* iOS background / gesture 待ち */ });
}

function initChat(): void {
  // 参加者リスト UI を bind
  const listEl = document.getElementById("participants-list") as HTMLElement | null;
  const countEl = document.getElementById("participants-count") as HTMLElement | null;
  if (listEl) participantsList = new ParticipantsList(listEl, countEl);

  connectWs();

  const form = document.getElementById("chat-form") as HTMLFormElement;
  const input = document.getElementById("chat-input") as HTMLInputElement;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text || ws?.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "CHAT", text, user: userId }));
    input.value = "";
  });
}

function connectWs(): void {
  if (document.visibilityState === "hidden") {
    // / と同じく background では new WebSocket を試さない (= iOS の即 close ループ対策)。
    // 復帰時に 1 度だけ呼び直す。waiter フラグで listener 重複登録を防ぐ
    // (Gemini medium 対応: 旧実装は connectWs が hidden で複数回呼ばれると listener が
    // 累積していた)。
    if (wsVisibilityWaiter) return;
    const onVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", onVisible);
      wsVisibilityWaiter = null;
      connectWs();
    };
    wsVisibilityWaiter = onVisible;
    document.addEventListener("visibilitychange", onVisible);
    return;
  }
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/ws`);
  ws.addEventListener("open", () => {
    wsBackoffMs = 5000;
    if (!visibilityTracker) {
      visibilityTracker = startVisibilityTracker((msg) => {
        if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
      });
    }
    visibilityTracker.notifyWsOpen();
  });
  ws.addEventListener("close", () => {
    const wait = wsBackoffMs + Math.floor(Math.random() * 2000);
    setTimeout(connectWs, wait);
    wsBackoffMs = Math.min(wsBackoffMs * 1.5, 30000);
  });
  ws.addEventListener("message", (ev) => {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(ev.data as string) as ServerMessage;
    } catch {
      return;
    }
    handleServerMessage(msg);
  });
}

function handleServerMessage(msg: ServerMessage): void {
  if (msg.type === "PLAY_SE" && msg.seId === "horn") {
    // 自分の click は initSE 側で local 即時再生済みなので broadcast loopback を skip
    // (= 二重再生防止、Gemini high 対応の片割れ)
    if (msg.user === userId) return;
    playHorn();
    return;
  }
  if (msg.type === "CHAT") {
    appendMessage("chat", `${msg.user ?? "anon"}: ${msg.text ?? ""}`);
    return;
  }
  if (msg.type === "SYSTEM") {
    appendMessage("system", msg.text ?? "");
    return;
  }
  if (
    msg.type === "USER_LIST" ||
    msg.type === "USER_JOIN" ||
    msg.type === "USER_LEAVE" ||
    msg.type === "USER_VISIBILITY"
  ) {
    participantsList?.handleMessage(msg);
  }
  // HEAT は /lite では表示しない (= シンプル UI 優先、必要なら後で追加)
}

function appendMessage(kind: "chat" | "system", text: string): void {
  const list = document.getElementById("messages")!;
  const el = document.createElement("div");
  el.className = `lite-msg lite-msg--${kind}`;
  el.textContent = text;
  list.appendChild(el);
  while (list.childElementCount > 200) list.firstElementChild?.remove();
  list.scrollTop = list.scrollHeight;
}

void boot();
